import { useState } from 'react';
import { CaretRight, Download, SignOut, Trash } from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { LogoutConfirmDialog } from '../../components/settings/LogoutConfirmDialog';
import { SettingsSubPageLayout } from '../../components/settings/SettingsSubPageLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { accountAPI } from '../../data/account';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { useAuth } from '../../store/AuthContext';

const CONFIRM_WORD = 'APAGAR';

export function DeleteAccountSettings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const canSubmit = confirmation === CONFIRM_WORD && password.length > 0 && !loading;

  const onDelete = async () => {
    if (!canSubmit) return;
    setLoading(true);
    const response = await accountAPI.deleteAccount({
      confirmation,
      current_password: password,
    });
    if (!response.success) {
      toast.error(response.message);
      setLoading(false);
      return;
    }
    toast.success(pt.settings.deleteAccount.success);
    await logout();
    navigate(paths.signIn);
  };

  return (
    <>
      <SettingsSubPageLayout
        section={pt.settings.deleteAccount.section}
        title={pt.settings.deleteAccount.title}
        subtitle={pt.settings.deleteAccount.subtitle}
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button type="button" variant="destructive" disabled={!canSubmit} onClick={onDelete}>
              {pt.settings.deleteAccount.submit}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(paths.settings)}>
              {pt.settings.deleteAccount.cancel}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-[17px] bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            <Trash className="h-7 w-7" aria-hidden />
          </div>
        </div>

        <ul className="space-y-2.5 rounded-2xl border border-border bg-muted/40 p-4 text-left text-xs leading-relaxed text-foreground">
          {pt.settings.deleteAccount.consequences.map((item) => (
            <li key={item} className="flex gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-700 dark:bg-rose-400" />
              <span>{item}</span>
            </li>
          ))}
          <li className="flex gap-2.5">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sage-600" />
            <span>{pt.settings.deleteAccount.keepNote}</span>
          </li>
        </ul>

        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80">
            {pt.settings.deleteAccount.alternativesTitle}
          </p>
          <a
            href={pt.settings.deleteAccount.exportHref}
            className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 text-left transition-colors hover:bg-muted/60"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-sage-100 text-sage-700 dark:bg-sage-950/40 dark:text-sage-300">
              <Download className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">
                {pt.settings.deleteAccount.exportTitle}
              </span>
              <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
                {pt.settings.deleteAccount.exportDesc}
              </span>
            </span>
            <CaretRight className="h-[18px] w-[18px] text-muted-foreground/40" aria-hidden />
          </a>
          <button
            type="button"
            onClick={() => setLogoutOpen(true)}
            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 text-left transition-colors hover:bg-muted/60"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground">
              <SignOut className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">
                {pt.settings.deleteAccount.logoutAltTitle}
              </span>
              <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
                {pt.settings.deleteAccount.logoutAltDesc}
              </span>
            </span>
            <CaretRight className="h-[18px] w-[18px] text-muted-foreground/40" aria-hidden />
          </button>
          <Link
            to="/privacy"
            className="block px-1 text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {pt.settings.hub.privacy.title}
          </Link>
        </div>

        <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
          <div>
            <Label htmlFor="delete-confirm">{pt.settings.deleteAccount.confirmLabel}</Label>
            <Input
              id="delete-confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              className="mt-2 font-mono"
              placeholder={CONFIRM_WORD}
            />
          </div>
          <div>
            <Label htmlFor="delete-password">{pt.settings.deleteAccount.passwordLabel}</Label>
            <PasswordInput
              id="delete-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-2"
            />
          </div>
        </div>
      </SettingsSubPageLayout>

      <LogoutConfirmDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </>
  );
}
